// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)

import React from "react";
import Plotly from "../../ThirdParty/PlotlyJS/plotly-v3.4.0-afPlots.min.js";
import createPlotlyComponent from "react-plotly.js/factory.js";
import type { PlotParams } from "react-plotly.js";
import "./AFPlotly.css";
import { BRAND_COLOR } from "../../../utilities/graphics";

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const Plot = createPlotlyComponent(Plotly);

const defaultFont = {
	family: '"Inter Tight", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
	size: 12,
	color: BRAND_COLOR,
};
const titleFont = { weight: 700 };
const axisDefaults = { showline: true, linecolor: BRAND_COLOR, linewidth: 1 };

const resolveTitle = (t: unknown): Record<string, unknown> => {
	if (!t) return { font: titleFont };
	if (typeof t === "string") return { text: t, font: titleFont };
	const obj = t as Record<string, unknown>;
	return { ...obj, font: { ...titleFont, ...(obj.font as Record<string, unknown> | undefined) } };
};

const computeYRange = (data: PlotParams["data"]): [number, number] | undefined => {
	if (!data?.some((t) => t.type === "box")) return undefined;
	const vals: number[] = [];
	data.forEach((t) => {
		const tr = t as Record<string, unknown>;
		if (t.type === "box") {
			for (const field of ["lowerfence", "upperfence"]) {
				const arr = tr[field] as number[] | undefined;
				if (arr) vals.push(...arr.filter((v): v is number => typeof v === "number" && !isNaN(v)));
			}
		}
		if (t.type === "scatter") {
			const y = tr.y as number[] | undefined;
			if (y) vals.push(...y.filter((v): v is number => typeof v === "number" && !isNaN(v)));
		}
	});
	if (!vals.length) return undefined;
	const min = vals.reduce((a, b) => Math.min(a, b), Infinity);
	const max = vals.reduce((a, b) => Math.max(a, b), -Infinity);
	const span = max - min;
	const pad = span === 0 ? Math.abs(max) * 0.1 || 1 : span * 0.15;
	return [min - pad, max + pad];
};

const buildLayout = (props: PlotParams): Record<string, unknown> => {
	const yRange = props.layout?.yaxis?.range ? undefined : computeYRange(props.data);
	return {
		...props.layout,
		font: { ...defaultFont, ...props.layout?.font },
		modebar: {
			bgcolor: "transparent",
			color: "rgba(68,68,68,0.3)",
			activecolor: "rgba(68,68,68,1)",
			...(props.layout?.modebar as Record<string, unknown> | undefined),
		},
		title: resolveTitle(props.layout?.title),
		xaxis: {
			...axisDefaults,
			zeroline: false,
			...props.layout?.xaxis,
			title: resolveTitle(props.layout?.xaxis?.title),
		},
		yaxis: {
			...axisDefaults,
			zeroline: false,
			...(yRange ? { range: yRange } : {}),
			...props.layout?.yaxis,
			title: resolveTitle(props.layout?.yaxis?.title),
		},
	};
};

const buildData = (props: PlotParams, disableHoverStyle: boolean): PlotParams["data"] => {
	if (!props.data?.length) return props.data ?? [];

	return props.data.map((t) => {
		const tr = t as Record<string, unknown>;
		const marker = (tr.marker as Record<string, unknown> | undefined) ?? {};

		if (disableHoverStyle) return t;

		if (tr.hoverlabel) return t;
		const color = (marker as { color?: unknown }).color;
		if (typeof color !== "string") return t;
		return { ...t, hoverlabel: { bgcolor: "white", bordercolor: color, font: { ...defaultFont } } };
	});
};

type AFPlotProps = (PlotParams | Readonly<PlotParams>) & { disableHoverStyle?: boolean };

const AFPlot = ({ disableHoverStyle = false, ...props }: AFPlotProps) => (
	<Plot {...props} data={buildData(props, disableHoverStyle)} layout={buildLayout(props)} />
);

export default AFPlot;
